import type { components } from '~/api/openapi';
import { formatDisplayName } from '~/lib/utils';

export type Reactor = components['schemas']['ReactionUserDto'] | undefined;

export type ReactionEntry = Record<
  string,
  { count: number; reactedByMe: boolean; reactors?: Reactor[] } & { className?: string }
>;

/**
 * Converts an array of emoji reactions from the API into a ReactionEntry object
 * for use with the Reactions component.
 */
export function convertToReactionEntry(
  emojiReactions: components['schemas']['EmojiReactionSummaryDto'][] | undefined,
  currentUserId: string | undefined
): ReactionEntry {
  const reactions = emojiReactions || [];
  return reactions.reduce<ReactionEntry>((acc, reaction) => {
    acc[reaction.emoji] = {
      count: reaction.reactedBy?.length || 0,
      reactedByMe: reaction.reactedBy?.some(u => u.id === currentUserId) ?? false,
      reactors: reaction.reactedBy?.filter(u => u.id !== currentUserId) || [],
    };
    return acc;
  }, {});
}

// Compute unique reactors and their display names
function getUniqueReactorsAndNames(
  reactionLogs: Array<{ reactedBy: components['schemas']['ReactionUserDto'] }>
) {
  const uniqueReactorsMap = new Map<string, components['schemas']['ReactionUserDto']>();
  reactionLogs.forEach(log => {
    if (!uniqueReactorsMap.has(log.reactedBy.id)) {
      uniqueReactorsMap.set(log.reactedBy.id, log.reactedBy);
    }
  });
  const allReactors = Array.from(uniqueReactorsMap.values());
  const reactorsNames = allReactors.map(getReactorName).filter(name => name.length > 0);
  return { allReactors, reactorsNames: reactorsNames };
}

function getReactorName(person: components['schemas']['ReactionUserDto'] | undefined) {
  if (!person) return '';
  const name = formatDisplayName(person.firstName, person.lastName);
  return name || (person as { email?: string }).email || '';
}

function getEmojisStringAndMostRecentEmoji(myEmojis: string[] | undefined) {
  const emojisString = myEmojis?.join(', ');
  const mostRecentEmoji = myEmojis?.at(-1);
  return { emojisString, mostRecentEmoji };
}
export { getUniqueReactorsAndNames, getReactorName, getEmojisStringAndMostRecentEmoji };
